import { action, computed, observable } from 'mobx'
import {
  getAllLocationZones,
  ICompanyRetailerId,
  ILocationZoneContract,
  LOCATION_ZONE_OPTION,
  updateLocationZones,
} from '@getgreenline/blaze-ecomm'
import { message } from 'antd-v4'
import { isEqual } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { LocationWithConfig, PolygonData } from './EcommerceSettingModels'

export class ConfigureEcomDelivery {
  FLAT_RATE_NAME = 'Flat rate'

  FLAT_RATE_DUMMY_POSTAL_CODE = 'Z9Z9Z9'

  config: LocationWithConfig

  private retailerId: ICompanyRetailerId

  @observable
  isUsingLegacyLocationZones = false

  /**
   * @description: {id: groupId, value: ILocationZoneContract}
   */
  @observable
  private mappedAllLocationZones = observable.map<string, ILocationZoneContract>()

  /**
   * @description: {id: groupId, value: ILocationZoneContract}
   */
  @observable
  private mappedLocationZonesBeforeUpdate = observable.map<string, ILocationZoneContract>()

  @observable
  private locationZoneOptionBeforeUpdate = LOCATION_ZONE_OPTION.FLAT_RATE

  @observable
  locationZoneOption = LOCATION_ZONE_OPTION.FLAT_RATE

  @observable
  activeKeys: string[] = []

  @observable
  isLoading = false

  @observable
  isSaving = false

  @observable
  canUseKMLFiles?: boolean | null

  constructor(config: LocationWithConfig, canUseKMLFiles?: boolean | null) {
    this.config = config

    this.retailerId = {
      companyId: `${config.companyId}`,
      locationId: `${config.id}`,
    }

    this.canUseKMLFiles = canUseKMLFiles

    this.getLocationZones()
  }

  @action
  private setIsUsingLegacyLocationZones = (isUsingLegacyLocationZones: boolean) => {
    this.isUsingLegacyLocationZones = isUsingLegacyLocationZones
  }

  @action
  private setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading
  }

  @action
  private setIsSaving = (isSaving: boolean) => {
    this.isSaving = isSaving
  }

  @action
  private setLocationZonesBeforeUpdate = (locationZonesBeforeUpdate?: ILocationZoneContract[]) => {
    this.mappedLocationZonesBeforeUpdate.clear()

    locationZonesBeforeUpdate?.forEach((locationZone) => {
      this.mappedLocationZonesBeforeUpdate.set(locationZone.groupId, locationZone)
    })
  }

  @action
  setAllLocationZones = (
    allLocationZones?: ILocationZoneContract[],
    updateIndexOrder?: boolean,
  ) => {
    this.mappedAllLocationZones.clear()

    let locationZones = allLocationZones || []

    if (locationZones.length && updateIndexOrder) {
      locationZones = this.updateIndexOrder(locationZones)
    }
    const covertedLocationZonePayload = this.convertToLocationZonePayload(locationZones)

    covertedLocationZonePayload.forEach((locationZone) => {
      this.mappedAllLocationZones.set(locationZone.groupId, locationZone)
    })
  }

  @action
  setActiveKeys = (activeKeys: string[]) => {
    this.activeKeys = activeKeys
  }

  @action
  setLocationZoneOptionBeforeUpdate = (locationZoneOptionBeforeUpdate: LOCATION_ZONE_OPTION) => {
    this.locationZoneOptionBeforeUpdate = locationZoneOptionBeforeUpdate
  }

  @action
  setLocationZoneOption = (locationZoneOption: LOCATION_ZONE_OPTION) => {
    this.locationZoneOption = locationZoneOption
  }

  @action
  reset = () => {
    const defaultLocationZoneOption = this.getInitialLocationZoneOption(
      this.locationZonesBeforeUpdate || [],
    )

    this.setActiveKeys([])
    this.setAllLocationZones(this.locationZonesBeforeUpdate)

    this.setLocationZoneOptionBeforeUpdate(defaultLocationZoneOption)
    this.setLocationZoneOption(defaultLocationZoneOption)
  }

  private getFirstLocationZone = (locationZones: ILocationZoneContract[]) => {
    return locationZones.find((lz) => lz.indexOrder === 1)
  }

  private getInitialLocationZoneOption = (
    locationZones: ILocationZoneContract[],
  ): LOCATION_ZONE_OPTION => {
    const firstLocationZone = this.getFirstLocationZone(locationZones)

    return firstLocationZone ? firstLocationZone.locationZoneOption : LOCATION_ZONE_OPTION.FLAT_RATE
  }

  private isFlatRatePostalCode = (locationZone: ILocationZoneContract) =>
    locationZone.name === this.FLAT_RATE_NAME &&
    (locationZone.postalCodes || []).length === 1 &&
    locationZone.postalCodes?.[0] === '*'

  private isFlatRateDummyPostalCode = (locationZone: ILocationZoneContract) =>
    locationZone.name === this.FLAT_RATE_NAME &&
    (locationZone.postalCodes || []).length === 1 &&
    locationZone.postalCodes?.[0] === this.FLAT_RATE_DUMMY_POSTAL_CODE

  // This will ensure the API is compatible with the legacy data
  private convertLegacyLocationZonePayload = (
    locationZone: ILocationZoneContract,
    firstLoad: boolean,
  ): LOCATION_ZONE_OPTION => {
    const { locationZoneOption, coordinates } = locationZone

    if (firstLoad) {
      this.setIsUsingLegacyLocationZones(!locationZoneOption)
    }

    if (locationZoneOption) return locationZoneOption

    let overrideLocationZoneOption = LOCATION_ZONE_OPTION.POSTAL_CODE

    if (coordinates?.length) {
      overrideLocationZoneOption = LOCATION_ZONE_OPTION.KML
    }

    if (this.isFlatRatePostalCode(locationZone) || this.isFlatRateDummyPostalCode(locationZone)) {
      overrideLocationZoneOption = LOCATION_ZONE_OPTION.FLAT_RATE
    }

    return overrideLocationZoneOption
  }

  // Convert the location zones to the proper format for lodash.isEqual()
  private convertToLocationZonePayload = (
    locationZoneContract: ILocationZoneContract[],
    firstLoad = false,
  ): ILocationZoneContract[] => {
    return locationZoneContract.map((locationZone) => {
      const locationZoneOption = this.convertLegacyLocationZonePayload(locationZone, firstLoad)

      const {
        name,
        fee,
        indexOrder,
        postalCodes,
        fillColor,
        lineColor,
        coordinates,
        groupId,
        companyId,
        locationId,
      } = locationZone

      return {
        locationZoneOption,
        name,
        fee,
        indexOrder,
        postalCodes,
        groupId,
        companyId,
        locationId,
        // API returns null for undefined values
        fillColor: fillColor || undefined,
        lineColor: lineColor || undefined,
        coordinates: coordinates || undefined,
      }
    })
  }

  private isFieldCompleted = ({
    locationZoneOption,
    name,
    postalCodes,
    coordinates,
  }: ILocationZoneContract) => {
    switch (locationZoneOption) {
      case LOCATION_ZONE_OPTION.FLAT_RATE:
        return name === this.FLAT_RATE_NAME
      case LOCATION_ZONE_OPTION.POSTAL_CODE:
        return name !== '' && postalCodes?.some((code) => code.trim() !== '')
      case LOCATION_ZONE_OPTION.KML:
        return name !== '' && (coordinates || []).length > 3
    }
  }

  private initializeFirstZoneIfNotExist = () => {
    const allLocationZones = this.addLocationZone({
      locationZoneOption: LOCATION_ZONE_OPTION.FLAT_RATE,
      name: this.FLAT_RATE_NAME,
      postalCodes: ['*'],
    })

    const responseAsLocationZonePayload = this.convertToLocationZonePayload(allLocationZones)
    this.setAllLocationZones(responseAsLocationZonePayload)
    this.setLocationZonesBeforeUpdate(responseAsLocationZonePayload)
  }

  private isAllFieldsCompleted = () => this.allLocationZones.every(this.isFieldCompleted)

  private expandFirstIncompleteZone = (locationZones: ILocationZoneContract[]) => {
    const firstIncompleteZoneIndex = locationZones.findIndex(
      (locationZone) => !this.isFieldCompleted(locationZone),
    )

    this.setActiveKeys([`${firstIncompleteZoneIndex}`])
  }

  private updateIndexOrder = (locationZones: ILocationZoneContract[]) => {
    return locationZones.map((locationZone, index) => ({
      ...locationZone,
      indexOrder: index + 1,
    }))
  }

  private getLocationZones = async () => {
    this.setIsLoading(true)

    try {
      const { data: locationZones } = await getAllLocationZones(this.retailerId)

      if (!locationZones.length) {
        return this.initializeFirstZoneIfNotExist()
      }

      const responseAsLocationZonePayload = this.convertToLocationZonePayload(locationZones, true)
      this.setLocationZonesBeforeUpdate(responseAsLocationZonePayload)
      this.setAllLocationZones(responseAsLocationZonePayload)

      const locationZoneOption = this.getInitialLocationZoneOption(responseAsLocationZonePayload)

      this.setLocationZoneOptionBeforeUpdate(locationZoneOption)
      this.setLocationZoneOption(locationZoneOption)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while loading Location zones')
    } finally {
      this.setIsLoading(false)
    }
  }

  private getAllowedLocationZonesToSave = (): ILocationZoneContract[] => {
    let updateLocationZoneObjects = [...this.allLocationZones]

    const filteredLocationZones = updateLocationZoneObjects
      .filter((lz) => lz.locationZoneOption === this.locationZoneOption)
      .map((lz) =>
        this.locationZoneOption === LOCATION_ZONE_OPTION.FLAT_RATE
          ? { ...lz, postalCodes: ['*'] }
          : lz,
      )

    // This is workaround if there is no zone and remove all the zones with the updated API
    if (filteredLocationZones.length === 0) {
      const fee = this.isUsingLegacyLocationZones ? this.flatRateObject?.fee || 0 : 0
      const postalCodes = this.isUsingLegacyLocationZones
        ? [this.FLAT_RATE_DUMMY_POSTAL_CODE]
        : undefined

      this.setAllLocationZones()

      updateLocationZoneObjects = this.addLocationZone({
        locationZoneOption: LOCATION_ZONE_OPTION.POSTAL_CODE,
        name: this.FLAT_RATE_NAME,
        postalCodes,
        fee,
      })
    }

    return updateLocationZoneObjects
  }

  // Used only for mapped location zone values
  private sortByIndexOrder = (locationZones: ILocationZoneContract[]) => {
    return locationZones.sort((a, b) => a.indexOrder - b.indexOrder)
  }

  saveLocationZones = async (promiseCb?: () => Promise<void>) => {
    try {
      this.setIsSaving(true)

      this.validateFieldCompletion()

      const locationZones = this.getAllowedLocationZonesToSave()

      const { data } = await updateLocationZones(
        this.retailerId,
        this.updateIndexOrder(locationZones),
      )

      if (promiseCb) {
        await promiseCb()
      }

      if (!locationZones.length) {
        return this.initializeFirstZoneIfNotExist()
      }

      const responseAsLocationZonePayload = this.convertToLocationZonePayload(data)
      this.setAllLocationZones(responseAsLocationZonePayload)
      this.setLocationZonesBeforeUpdate(responseAsLocationZonePayload)
      this.setActiveKeys([])

      const locationZoneOption = this.getInitialLocationZoneOption(responseAsLocationZonePayload)

      this.setLocationZoneOption(locationZoneOption)
      this.setLocationZoneOptionBeforeUpdate(locationZoneOption)
      message.success(`Location zones successfully saved.`, 2)
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error while updating Location zones')
    } finally {
      this.setIsSaving(false)
    }
  }

  validateFieldCompletion = () => {
    if (!this.isAllFieldsCompleted()) {
      this.expandFirstIncompleteZone(this.locationZonesToShow)
      throw new Error('All fields are mandatory')
    }
  }

  toggleActiveKeys = () => {
    const newKeys =
      this.activeKeys.length > 0 ? [] : this.allLocationZones.map((l, index) => `${index}`)

    this.setActiveKeys(newKeys)
  }

  addLocationZone = (override?: Partial<ILocationZoneContract>): ILocationZoneContract[] => {
    const updatedLocationZones = [...this.allLocationZones]
    updatedLocationZones.unshift({
      locationZoneOption: this.locationZoneOption,
      name: '',
      fee: 0,
      indexOrder: 1,
      postalCodes: [],
      fillColor: undefined,
      lineColor: undefined,
      coordinates: undefined,
      groupId: uuidv4(),
      companyId: this.config.companyId,
      locationId: this.config.id,
      ...override,
    })

    this.setAllLocationZones(updatedLocationZones)

    return updatedLocationZones
  }

  uploadKMLZone = (data: PolygonData, currentLocationZone?: ILocationZoneContract) => {
    if (!currentLocationZone) {
      return this.addLocationZone({ ...data })
    }

    const { groupId } = currentLocationZone

    return this.mappedAllLocationZones.set(groupId, {
      ...currentLocationZone,
      ...data,
    })
  }

  uploadMultipleKMLZones = (data: PolygonData[]) => {
    this.setAllLocationZones()

    for (const polygonData of data) {
      this.uploadKMLZone(polygonData)
    }

    this.setActiveKeys([])
  }

  @computed
  get allLocationZones(): ILocationZoneContract[] {
    return this.sortByIndexOrder(Array.from(this.mappedAllLocationZones.values()))
  }

  @computed
  get locationZonesBeforeUpdate(): ILocationZoneContract[] {
    return this.sortByIndexOrder(Array.from(this.mappedLocationZonesBeforeUpdate.values()))
  }

  @computed
  get isEcomDeliveryConfigured(): boolean {
    return this.allLocationZones.length > 0
  }

  @computed
  get locationZonesToShow(): ILocationZoneContract[] {
    return this.allLocationZones.filter((lz) => {
      if (lz.name !== this.FLAT_RATE_NAME) return true

      return lz.postalCodes && !this.isFlatRatePostalCode(lz) && !this.isFlatRateDummyPostalCode(lz)
    })
  }

  // 'Flat rate' is a special location zone with a fee applied to all postal codes, treated as a regular zone in the backend.
  @computed
  get flatRateObject(): ILocationZoneContract | undefined {
    return this.allLocationZones.find(
      (lz) => lz.locationZoneOption === LOCATION_ZONE_OPTION.FLAT_RATE,
    )
  }

  @computed
  get isLocationZonesModified(): boolean {
    return (
      this.locationZoneOptionBeforeUpdate !== this.locationZoneOption ||
      !isEqual(this.locationZonesBeforeUpdate, this.allLocationZones)
    )
  }

  @computed
  get hasOtherLocationZones(): boolean {
    return this.allLocationZones.some((lz) => lz.locationZoneOption !== this.locationZoneOption)
  }

  @computed
  get isFlatRateOptionEnabled(): boolean {
    return this.locationZoneOption === LOCATION_ZONE_OPTION.FLAT_RATE
  }

  @computed
  get isPostalCodeOptionEnabled(): boolean {
    return this.locationZoneOption === LOCATION_ZONE_OPTION.POSTAL_CODE
  }

  @computed
  get isKMLOptionEnabled(): boolean {
    return this.locationZoneOption === LOCATION_ZONE_OPTION.KML
  }
}
